import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Link, Navigate, NavLink, useNavigate, useParams } from "react-router-dom";
import { GearSix } from "@phosphor-icons/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { AdminAudienceState } from "../../shared/types.ts";
import { useRoleAuth } from "../auth/AuthGate.tsx";
import { SignOutButton } from "../auth/SignOutButton.tsx";
import {
  buildAdminPath,
  buildAdminSummaryPath,
  buildJoinPath,
  buildModeratorPath,
  buildProjectorPath,
} from "../lib/join.ts";
import { resolveSessionViewRoute } from "../lib/sessionViewRoute.ts";
import { notificationCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import { useOptimisticAudienceState } from "../lib/useOptimisticAudienceState.ts";
import { useSessionTranscript } from "../lib/useSessionTranscript.ts";
import { useStaffSessionRoute } from "../lib/useStaffSessionRoute.ts";
import { RecapPanel } from "../components/RecapPanel.tsx";
import { AudienceCategoriesCard } from "./AudienceCategoriesCard.tsx";
import { FunctionBar } from "./FunctionBar.tsx";
import { GearMenu } from "./GearMenu.tsx";
import { NotesPanel } from "./NotesPanel.tsx";
import { PanelInfoCard, type PanelInfoSavePatch, type PanelistSaveInput } from "./PanelInfoCard.tsx";
import type { AssignSpeakerAliasInput, ReplacePanelistAliasesInput } from "./SpeakerAliasControls.tsx";
import { ProjectorCard } from "./ProjectorCard.tsx";
import { SessionControl, type SessionListItemLite } from "./SessionControl.tsx";
import {
  SessionContentResetDialog,
  type SessionContentResetSelection,
} from "./SessionContentResetDialog.tsx";
import { NoActiveSession, StaffSessionUnavailable } from "./SessionGates.tsx";
import { StratifiedBoard } from "./StratifiedBoard.tsx";
import { TranscriptPanel } from "./TranscriptPanel.tsx";
import { useOperatorState } from "./useOperatorState.ts";
import { useSessionSpeakers } from "./useSessionSpeakers.ts";

type SessionListItemWithActive = SessionListItemLite & { active: boolean };

const DEFAULT_SESSION_TITLE = "New session";

function sessionOptionLabel(session: SessionListItemLite): string {
  return [
    session.title,
    session.joinCode ? `#${session.joinCode}` : null,
    session.kind,
    session.status,
    new Date(session.startedAt).toLocaleString(),
  ].filter(Boolean).join(" · ");
}

export function AdminDashboard() {
  const auth = useRoleAuth("operator");
  const navigate = useNavigate();
  const { "*": routeSuffix } = useParams();
  const sessionViewRoute = resolveSessionViewRoute(routeSuffix);
  const sessionRoute = useStaffSessionRoute();
  const routeSessionArgs = sessionRoute.hasJoinCodeRoute
    ? sessionRoute.sessionId ? { auth, sessionId: sessionRoute.sessionId } : "skip"
    : { auth };

  const { viewState, health } = useOperatorState(auth, routeSessionArgs);
  const sessions = useQuery(convexApi.sessions.getSessionList, {
    auth,
    includeInactiveTestSessions: false,
  }) as SessionListItemWithActive[] | undefined;
  const liveSession = sessions?.find((session) => session.active) ?? null;
  const audience = useQuery(convexApi.audience.getStaffAudienceState, routeSessionArgs) as
    | AdminAudienceState
    | null
    | undefined;
  const optimisticAudience = useOptimisticAudienceState(auth, audience);
  const resetStatus = useQuery(
    convexApi.sessions.getContentResetStatus,
    viewState ? { auth, sessionId: viewState.meta.id as Id<"sessions"> } : "skip",
  );

  const createSession = useMutation(convexApi.sessions.create);
  const setActiveSession = useMutation(convexApi.sessions.setActive);
  const closeSession = useMutation(convexApi.sessions.close);
  const issueLiveSessionHandoff = useMutation(convexApi.sessions.issueLiveSessionHandoff);
  const startContentReset = useMutation(convexApi.sessions.startContentReset);
  const updateSessionTitle = useMutation(convexApi.sessions.updateTitle);
  const saveGenerationConfig = useMutation(convexApi.settings.setGenerationConfig);
  const saveRosterPanelist = useMutation(convexApi.speakerMappings.savePanelist);
  const removeRosterPanelist = useMutation(convexApi.speakerMappings.removePanelist);
  const assignSpeakerAliasMutation = useMutation(convexApi.speakerMappings.assignPanelist);
  const replacePanelistAliasesMutation = useMutation(convexApi.speakerMappings.replacePanelistAliases);
  const setNotes = useMutation(convexApi.settings.setNotes);

  const transcriptFeed = useSessionTranscript({
    auth,
    sessionId: viewState?.meta.id ?? null,
    enabled: Boolean(viewState),
    transcriptProjectionVersion: viewState?.status.transcriptProjectionVersion ?? 0,
  });
  const speakers = useSessionSpeakers({
    auth,
    sessionId: viewState?.meta.id ?? null,
    entries: transcriptFeed.entries,
    enabled: Boolean(viewState),
  });

  const [gearOpen, setGearOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [interim, setInterim] = useState("");
  const [transcriptOpen, setTranscriptOpen] = useState(true);

  // Canonicalize bare /admin onto the session-scoped URL once the shell state
  // resolves.
  useEffect(() => {
    if (sessionRoute.hasJoinCodeRoute) return;
    if (!viewState?.meta.joinCode) return;
    navigate(buildAdminPath(viewState.meta.joinCode), { replace: true });
  }, [navigate, sessionRoute.hasJoinCodeRoute, viewState?.meta.joinCode]);

  // Amber attention dot condition: a shared re-consolidation preview is pending.
  const summariesAttention: boolean = viewState?.status.consolidation?.previewPending === true;

  async function createNewSession() {
    await createSession({
      auth,
      title: DEFAULT_SESSION_TITLE,
      kind: "live",
      provider: "groq",
      setActive: true,
    });
    if (sessionRoute.hasJoinCodeRoute) navigate("/admin", { replace: true });
  }

  function viewSession(sessionId: Id<"sessions">) {
    const selected = sessions?.find((session) => session.id === sessionId);
    if (!selected?.joinCode) {
      navigate("/admin", { replace: true });
      return;
    }
    navigate(buildAdminPath(selected.joinCode), { replace: true });
  }

  async function makeSessionLive(sessionId: Id<"sessions">) {
    if (liveSession?.id !== sessionId) await setActiveSession({ auth, sessionId });
    const selected = sessions?.find((session) => session.id === sessionId);
    navigate(selected?.joinCode ? buildAdminPath(selected.joinCode) : "/admin", { replace: true });
  }

  async function endLiveSession(sessionId: Id<"sessions">) {
    await closeSession({ auth, sessionId });
  }

  async function bringEveryoneHere() {
    await issueLiveSessionHandoff({ auth });
    notify.success(notificationCopy.admin.switchCommandSent);
  }

  async function clearSessionContent(selection: SessionContentResetSelection): Promise<string> {
    if (!viewState) throw new Error("Session is not available.");
    return await startContentReset({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      ...selection,
    });
  }

  async function saveSessionTitle(sessionId: string, title: string) {
    await updateSessionTitle({ auth, sessionId: sessionId as Id<"sessions">, title });
  }

  async function savePanelInfo(patch: PanelInfoSavePatch) {
    if (!viewState) return;
    await saveGenerationConfig({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      purpose: patch.purpose ?? viewState.generation.purpose,
      themes: patch.themes ?? viewState.generation.themes,
      thresholdSeconds: viewState.generation.thresholdSeconds,
      thresholdWords: viewState.generation.thresholdWords,
      judgeEnabled: viewState.generation.judgeEnabled,
      aiDebugEnabled: viewState.generation.aiDebugEnabled,
    });
  }

  async function savePanelist(input: PanelistSaveInput) {
    if (!viewState) return;
    await saveRosterPanelist({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      originalName: input.originalName,
      isModerator: input.panelist.isModerator ?? false,
      name: input.panelist.name,
      bio: input.panelist.bio,
    });
  }

  async function removePanelist(name: string) {
    if (!viewState) return;
    await removeRosterPanelist({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      name,
    });
  }

  async function assignSpeakerAlias(input: AssignSpeakerAliasInput) {
    if (!viewState) return;
    await assignSpeakerAliasMutation({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      speakerKey: input.speakerKey,
      name: input.name,
      ...(input.bio === undefined ? {} : { bio: input.bio }),
    });
  }

  async function replacePanelistAliases(input: ReplacePanelistAliasesInput) {
    if (!viewState) return;
    await replacePanelistAliasesMutation({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      name: input.name,
      speakerKeys: input.speakerKeys,
    });
  }

  async function saveAiDebugEnabled(aiDebugEnabled: boolean) {
    if (!viewState) return;
    await saveGenerationConfig({
      auth,
      sessionId: viewState.meta.id as Id<"sessions">,
      purpose: viewState.generation.purpose,
      themes: viewState.generation.themes,
      thresholdSeconds: viewState.generation.thresholdSeconds,
      thresholdWords: viewState.generation.thresholdWords,
      judgeEnabled: viewState.generation.judgeEnabled,
      aiDebugEnabled,
    });
  }

  if (sessionRoute.notFound) return <StaffSessionUnavailable />;
  if (sessionRoute.resolving) return <div className="loading">Opening session...</div>;
  if (viewState === undefined) return <div className="loading">Connecting to session...</div>;
  if (viewState === null && sessionRoute.hasJoinCodeRoute) return <StaffSessionUnavailable />;
  if (viewState === null) {
    return (
      <NoActiveSession
        sessions={sessions}
        optionLabel={sessionOptionLabel}
        onCreate={() => void createNewSession()}
        onActivate={(sessionId) => void makeSessionLive(sessionId)}
      />
    );
  }

  const adminBasePath = buildAdminPath(viewState.meta.joinCode);
  const timelinePath = viewState.meta.joinCode
    ? buildAdminSummaryPath(viewState.meta.joinCode, "timeline")
    : adminBasePath;
  const consolidatedPath = viewState.meta.joinCode
    ? buildAdminSummaryPath(viewState.meta.joinCode, "consolidated")
    : adminBasePath;
  const themesPath = viewState.meta.joinCode
    ? buildAdminSummaryPath(viewState.meta.joinCode, "themes")
    : adminBasePath;
  if (sessionViewRoute.kind === "redirect") {
    return (
      <Navigate
        to={sessionViewRoute.destination === "timeline" ? timelinePath : adminBasePath}
        replace
      />
    );
  }
  const workspaceTab = sessionViewRoute.kind === "summary" ? "summaries" : "board";
  const summaryPage = sessionViewRoute.kind === "summary" ? sessionViewRoute.page : "timeline";

  return (
    <main className="operator admin2-shell" data-testid="admin2-shell">
      <header className="topbar admin2-topbar" data-testid="admin2-topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">Admin View</span>
          </div>
        </div>

        <SessionControl
          selectedId={viewState.meta.id as Id<"sessions">}
          liveId={liveSession?.id ?? null}
          title={viewState.meta.title}
          sessions={sessions}
          optionLabel={sessionOptionLabel}
          onRename={(t) => saveSessionTitle(viewState.meta.id, t)}
          onSelect={(id) => viewSession(id)}
          onMakeLive={makeSessionLive}
          onEndLive={endLiveSession}
          onBringEveryoneHere={bringEveryoneHere}
          onNew={() => void createNewSession()}
        />

        {viewState.meta.joinCode && (
          <span className="admin2-join">join <b data-testid="audience-join-code">{viewState.meta.joinCode}</b></span>
        )}

        <div className="admin2-spacer" />

        <nav className="admin2-view-links" aria-label="Live views">
          <a data-testid="open-moderator" href={buildModeratorPath(viewState.meta.joinCode)} target="_blank" rel="noreferrer">Moderator ↗</a>
          <a data-testid="open-projector" href={buildProjectorPath(viewState.meta.joinCode)} target="_blank" rel="noreferrer">Projector ↗</a>
          <a data-testid="open-participants" href={viewState.meta.joinCode ? buildJoinPath(viewState.meta.joinCode) : "/join"} target="_blank" rel="noreferrer">Participants ↗</a>
        </nav>

        <div className="admin2-debug-tools">
          <label className="switch admin2-debug-switch" title="Verbose AI logs">
            <span>AI logs</span>
            <input
              data-testid="generation-ai-debug-enabled"
              type="checkbox"
              checked={viewState.generation.aiDebugEnabled}
              onChange={(event) => void saveAiDebugEnabled(event.target.checked)}
            />
          </label>
          <a
            className="admin2-debug-link"
            data-testid="open-prompt-diagnostics"
            href="/admin/llm-settings/diagnostics?workflow=all"
          >
            Diagnostics
          </a>
        </div>

        <button
          type="button"
          className="icon-btn"
          data-testid="admin2-gear"
          data-gear-menu-trigger
          title="Session menu"
          aria-expanded={gearOpen}
          onClick={() => setGearOpen((open) => !open)}
        >
          <GearSix />
        </button>
        {gearOpen && (
          <GearMenu
            sessionId={viewState.meta.id}
            onClearContent={() => setResetOpen(true)}
            onClose={() => setGearOpen(false)}
          />
        )}
        <SignOutButton redirectTo="/admin" />
      </header>

      <FunctionBar
        auth={auth}
        state={viewState}
        health={health}
        transcriptEntries={transcriptFeed.entries}
        transcriptTotalEntries={transcriptFeed.summary?.totalEntries ?? null}
        aiThreadCount={optimisticAudience.state?.threads.filter((t) => t.threadOrigin === "ai").length ?? 0}
        recapCount={optimisticAudience.state?.recaps.length ?? 0}
        lastGenerationSummary={viewState.status.lastGenerationSummary ?? null}
        onInterim={setInterim}
        onEnsureSessionActive={async (sessionId) => {
          await makeSessionLive(sessionId as Id<"sessions">);
        }}
      />

      {resetOpen && (
        <SessionContentResetDialog
          sessionTitle={viewState.meta.title}
          captureRunning={viewState.status.captureState !== "stopped"}
          status={resetStatus ?? null}
          onStart={clearSessionContent}
          onClose={() => setResetOpen(false)}
        />
      )}

      <div className={`admin2-body ${transcriptOpen ? "" : "transcript-collapsed"}`}>
        <div className="admin2-col admin2-rail-left" data-testid="admin2-rail-left">
          <ProjectorCard
            projector={optimisticAudience.state?.projector ?? null}
            threads={optimisticAudience.state?.threads ?? []}
            artifacts={optimisticAudience.state?.artifacts ?? []}
            actions={optimisticAudience.projectorActions}
            pending={optimisticAudience.pending.projector}
          />
          <PanelInfoCard
            meta={viewState.meta}
            generation={viewState.generation}
            onSave={(patch) => void savePanelInfo(patch)}
            onSavePanelist={savePanelist}
            onRemovePanelist={removePanelist}
            speakers={speakers}
            onAssignSpeakerAlias={assignSpeakerAlias}
            onReplacePanelistAliases={replacePanelistAliases}
          />
          {audience && (
            <AudienceCategoriesCard
              auth={auth}
              sessionId={viewState.meta.id}
              sessionTitle={viewState.meta.title}
              generationPurpose={viewState.generation.purpose}
              state={audience}
            />
          )}
          <NotesPanel
            sessionId={viewState.meta.id}
            notes={viewState.notes}
            onSetNotes={(notes) => void setNotes({
              auth,
              sessionId: viewState.meta.id as Id<"sessions">,
              notes,
            })}
          />
        </div>
        <div className="admin2-col admin2-center" data-testid="admin2-center">
          {optimisticAudience.state && (
            <>
              <div className="center-sticky">
                <nav className="workspace-tabs" aria-label="Center workspace pages">
                  <Link
                    to={adminBasePath}
                    aria-current={workspaceTab === "board" ? "page" : undefined}
                    className={`wtab ${workspaceTab === "board" ? "on" : ""}`}
                    data-testid="workspace-tab-board"
                  >
                    Board <span className="cnt">{optimisticAudience.state.threads.filter((t) => t.status === "shown").length}</span>
                  </Link>
                  <Link
                    to={timelinePath}
                    aria-current={workspaceTab === "summaries" ? "page" : undefined}
                    className={`wtab ${workspaceTab === "summaries" ? "on" : ""}`}
                    data-testid="workspace-tab-summaries"
                  >
                    Summaries
                    {summariesAttention ? (
                      <span className="attn-dot" data-testid="summaries-attn-dot" title="A consolidation preview is ready" />
                    ) : null}
                  </Link>
                </nav>
              </div>
              {workspaceTab === "board" ? (
                <StratifiedBoard
                  auth={auth}
                  sessionId={viewState.meta.id}
                  state={optimisticAudience.state}
                  actions={optimisticAudience.threadActions}
                  pending={optimisticAudience.pending}
                  joinedCount={viewState.status.connectedClients}
                />
              ) : (
                <div className="admin2-summaries" data-testid="admin2-summaries">
                  <RecapPanel
                    auth={auth}
                    sessionId={viewState.meta.id}
                    recaps={optimisticAudience.state.recaps}
                    title="Panel summaries"
                    operator
                    operatorPage={summaryPage}
                    operatorNavigation={(
                      <nav className="segmented secondary oval" aria-label="Summary pages">
                        <NavLink to={timelinePath}>Timeline</NavLink>
                        <NavLink to={consolidatedPath}>Consolidated</NavLink>
                        <NavLink to={themesPath}>Themes</NavLink>
                      </nav>
                    )}
                    coverage={viewState.status.recapCoverage ?? null}
                  />
                </div>
              )}
            </>
          )}
        </div>
        <div className="admin2-col admin2-rail-right" data-testid="admin2-rail-right">
          <TranscriptPanel
            interim={interim}
            status={viewState.status}
            sessionId={viewState.meta.id}
            auth={auth}
            speakers={speakers}
            feed={transcriptFeed}
            open={transcriptOpen}
            onToggle={() => setTranscriptOpen((v) => !v)}
          />
        </div>
      </div>
    </main>
  );
}
